import { useState } from 'react';
import type { AdminUserPublic } from '@/api/users';
import { changePassword, deleteUser } from '@/api/users';
import { useAuth } from '@/auth/AuthContext';

interface Props {
  users: AdminUserPublic[];
  onChanged: () => void;
}

export function UsersTable({ users, onChanged }: Props) {
  const { user: current, logout } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pwFor, setPwFor] = useState<string | null>(null);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');

  const handleDelete = async (username: string) => {
    if (users.length <= 1) {
      setError('Cannot delete the last admin.');
      return;
    }
    const isSelf = username === current?.username;
    const confirmMsg = isSelf
      ? `Delete your own account "${username}"? You will be signed out.`
      : `Delete admin "${username}"?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(username);
    setError(null);
    try {
      await deleteUser(username);
      if (isSelf) {
        await logout();
      }
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const submitPassword = async () => {
    if (!pwFor) return;
    if (pwNew.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setBusy(pwFor);
    setError(null);
    try {
      const isSelf = pwFor === current?.username;
      await changePassword(pwFor, pwNew, isSelf ? pwCurrent : undefined);
      setPwFor(null);
      setPwCurrent('');
      setPwNew('');
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Username</th>
              <th className="text-left px-4 py-2 font-medium">Created</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username} className="border-t border-slate-100">
                <td className="px-4 py-2 text-slate-900">
                  {u.username}
                  {u.username === current?.username && (
                    <span className="ml-2 text-xs text-slate-500">(you)</span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-500">
                  {new Date(u.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => {
                      setPwFor(u.username);
                      setPwCurrent('');
                      setPwNew('');
                    }}
                    disabled={busy === u.username}
                    className="mr-2 px-3 py-1 text-xs rounded-md border border-slate-300 hover:bg-slate-50"
                  >
                    Change password
                  </button>
                  <button
                    onClick={() => handleDelete(u.username)}
                    disabled={busy === u.username || users.length <= 1}
                    className="px-3 py-1 text-xs rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwFor && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-900">Change password for {pwFor}</h3>
          {pwFor === current?.username && (
            <div>
              <label className="block text-xs text-slate-600 mb-1">Current password</label>
              <input
                type="password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-slate-600 mb-1">New password (min 8 chars)</label>
            <input
              type="password"
              value={pwNew}
              onChange={(e) => setPwNew(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={submitPassword}
              disabled={busy === pwFor}
              className="px-3 py-1.5 rounded-md bg-brand-500 text-slate-900 text-sm hover:bg-brand-600 disabled:opacity-50"
            >
              Save password
            </button>
            <button
              onClick={() => setPwFor(null)}
              className="px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
